import type { MailboxInfo } from "owa-client-types";
import type { TokenResponseExtended as TokenResponse } from "owa-service/lib/tokenResponseExtended";
import { getRealmFromHeader } from "owa-tokenprovider-utils";
import type { AtLeastOne, AuthenticationTargets, silentlyGetResourceTokenFunc, } from "owa-auth-callbacks";
import { getAccessTokenforResourceAsLazy } from "./getAccessTokenforResource";
import type { AuthTokenResponse } from "./schema/AuthTokenResponse";
import type TokenResponseCode from "owa-service/lib/contract/TokenResponseCode";

/**
 * Tries to silently get an access token based on the information in targets.
 * @param action. A helpful string, like the one passed to makeServiceRequest, to help identify the context of the request.
 * @param mailboxInfo. Mailbox context to use for this resource token request.
 * @param targets. At least one of three parameters must be provided: resource, scope, or wwwAuthenticateHeader.
 */
export const silentlyGetResourceToken: silentlyGetResourceTokenFunc = async function (action: string, mailboxInfo: MailboxInfo, targets: AtLeastOne<AuthenticationTargets>): Promise<TokenResponse | undefined> {
    if (!action) {
        throw new Error("Action must be provided for proper attribution.");
    }
    
    const realm = getRealmFromHeader(targets.wwwAuthenticateHeader) ?? "";
    if (!realm && !targets.resource && !targets.scope) {
        throw new Error("WWW-AuthenticateHeader must have a realm property if no resource/scope are specified to resolve an auth challenge.");
    }
    
    const resource = targets.resource ?? realm;
    
    const [, authTokenResponsePromise] = getAccessTokenforResourceAsLazy(resource, action, undefined /* requestId */, undefined /*targetTenantId*/, targets.wwwAuthenticateHeader, true /*showFullTokenResponse*/, undefined /*preferIdpToken*/, mailboxInfo, targets.scope, undefined /*sendClientCapabilityClaim*/, undefined /*claims, should be included in the www-authenticate headers*/, true /*suppressInteractiveFallback*/);
    
    if (!authTokenResponsePromise) {
        return undefined;
    }
    
    return authTokenResponsePromise.then(unspecifiedAuthTokenResponse => {
        const authTokenResponseResult = unspecifiedAuthTokenResponse as AuthTokenResponse | undefined;
        if (!authTokenResponseResult) {
            return undefined;
        }
        
        const responseCode: TokenResponseCode | undefined = authTokenResponseResult.accessToken
            ? 0 : 3;
        
        return {
            TokenResultCode: responseCode,
            AccessToken: authTokenResponseResult.accessToken,
            AccessTokenExpiry: authTokenResponseResult.accessTokenExpiry,
            ExpiresIn: authTokenResponseResult.expiresIn,
            SubErrorCode: authTokenResponseResult.error?.subErrorCode,
        };
    });
};
